import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useParams, useResolvedPath } from 'react-router-dom';
import { invariant } from '@utils/invariant';
import * as Yup from 'yup';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/button/Button';
import { DataField } from '../../../../components/DataField';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { InputDialog } from '../../../../components/dialog/InputDialog';
import { PageHeader } from '../../../../components/PageHeader';
import { ITabItem, Tabs } from '../../../../components/tabs/Tabs';
import {
  SupplierDocumentType,
  useSupplierById,
  linkSupplierDocument,
  unlinkSupplierDocument,
  updateSupplier,
  type SupplierDetailEntry,
} from '../../models';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import { getDisplayError } from '../../../../utils/get-display-error';
import { FileUploaderButton } from '../../../document/components/FileUploaderButton';
import { MultiFileUploaderButton } from '../../../document/components/MultiFileUploaderButton';
import { SupplierProvider } from './supplierContext';
import { useAuth } from '../../../../contexts/auth-context';
import { PageHeading } from 'components/PageHeading';
import { captureException } from '@sentry/react';
import { roleToNumber } from 'src/app/auth/roles.client';
import { ErrorState } from 'components/error-state/ErrorState';
import { DelayedSpinner } from 'components/Spinner';

export type Supplier = SupplierDetailEntry;

const SupplierPage = () => {
  const { me } = useAuth();
  const { supplierId } = useParams<{ supplierId: string }>();
  invariant(supplierId);
  const { data, error, isLoading, mutate } = useSupplierById(+supplierId);

  const generalRoute = useResolvedPath('general');
  const contactsRoute = useResolvedPath('contacts');
  const usersRoute = useResolvedPath('users');
  const driverDocumentsRoute = useResolvedPath('driver-documents');
  const trucksRoute = useResolvedPath('trucks');
  const trackingRoute = useResolvedPath('tracking');
  const auditLogsRoute = useResolvedPath('audit-logs');
  const tabItems = useMemo<ITabItem[]>(() => {
    const res = [
      {
        title: 'Algemeen',
        path: generalRoute.pathname,
      },
      {
        title: 'Gebruikers Carrier Portal',
        path: usersRoute.pathname,
      },
      {
        title: 'Chauffeurdocumenten',
        path: driverDocumentsRoute.pathname,
      },
      {
        title: 'Contacten',
        path: contactsRoute.pathname,
      },
      {
        title: 'Trekkers',
        path: trucksRoute.pathname,
      },
      {
        title: 'Tracking',
        path: trackingRoute.pathname,
      },
    ];

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin)) {
      res.push({
        title: 'Audit logs',
        path: auditLogsRoute.pathname,
      });
    }

    return res;
  }, []);

  const refreshData = useCallback(() => {
    mutate();
  }, [mutate]);

  const supplier = data;

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  if (!supplier) {
    return (
      <ErrorState
        error={error}
        title={error ? 'Vervoerder kon niet worden geladen' : 'Vervoerder niet gevonden'}
        description={error ? getDisplayError(error) : 'De gevraagde vervoerder werd niet gevonden.'}
        onRetry={error ? refreshData : undefined}
        variant="page"
      />
    );
  } else {
    return (
      <>
        <SupplierProvider supplier={supplier} refreshData={refreshData}>
          <PageHeader title={supplier.name} />

          <PageHeading
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Vervoerders',
                  to: '/internal/suppliers',
                }}
                currentItem={supplier.name}
              />
            }
            rightSide={
              <div className="flex gap-2">
                {supplier.deactivationReason ? (
                  <ConfirmDialog
                    triggerText="Heractiveer vervoerder"
                    title="Heractiveer vervoerder"
                    submitText="Heractiveer"
                    description="Ben je zeker dat je de vervoerder wilt heractiveren?"
                    onSubmit={async () => {
                      try {
                        await updateSupplier(supplier.id, {
                          deactivationReason: null,
                        });
                        refreshData();
                        toast.success('Vervoerder is geheractiveerd');
                      } catch (err) {
                        captureException(err);
                        toast.error('Vervoerder heractiveren mislukt: ' + getDisplayError(err));
                      }
                    }}
                  />
                ) : (
                  <InputDialog
                    triggerText="Deactiveer vervoerder"
                    title="Deactiveer vervoerder"
                    submitText="Deactiveer"
                    description="Ben je zeker dat je de vervoerder wilt deactiveren?"
                    labelText="Reden"
                    validation={Yup.string().min(5, 'Minimum 5 karakters')}
                    onSubmit={async (value: string) => {
                      try {
                        await updateSupplier(supplier.id, {
                          deactivationReason: value,
                        });
                        refreshData();
                        toast.success('Vervoerder is gedeactiveerd');
                      } catch (err) {
                        captureException(err);
                        toast.error('Vervoerder deactiveren mislukt: ' + getDisplayError(err));
                        throw err;
                      }
                    }}
                  />
                )}

                <Button color="secondary" onTrigger={refreshData}>
                  <ArrowClockwiseIcon className="button-icon" />
                </Button>
              </div>
            }
          />

          <div className="px-4">
            <div className="flex justify-between items-center my-4">
              {!!supplier.deactivationReason && (
                <div className="w-full flex mt-2">
                  <DataField title="Deactivatie reden">{supplier.deactivationReason}</DataField>
                </div>
              )}

              <div className="flex gap-2 self-end">
                <FileUploaderButton
                  buttonText="BA Verzekering"
                  title="BA Verzekering"
                  initialName={`BA Verzekering - ${supplier.id}`}
                  file={
                    supplier.documents.find((d) => d.type === SupplierDocumentType.CivilLiabilityInsurance)?.document
                  }
                  onSubmit={async (document) => {
                    await linkSupplierDocument(supplier.id, document.id, SupplierDocumentType.CivilLiabilityInsurance);
                    refreshData();
                    toast.success('AB Verzekering opgeladen');
                  }}
                />

                <FileUploaderButton
                  buttonText="CMR Verzekering"
                  title="CMR Verzekering"
                  initialName={'CMR Verzekering'}
                  file={supplier.documents.find((d) => d.type === SupplierDocumentType.CmrInsurance)?.document}
                  onSubmit={async (document) => {
                    await linkSupplierDocument(supplier.id, document.id, SupplierDocumentType.CmrInsurance);
                    refreshData();
                  }}
                />

                <FileUploaderButton
                  buttonText="Enquete"
                  title="Enquete"
                  initialName={'Enquete'}
                  file={supplier.documents.find((d) => d.type === SupplierDocumentType.Survey)?.document}
                  onSubmit={async (document) => {
                    await linkSupplierDocument(supplier.id, document.id, SupplierDocumentType.Survey);
                    refreshData();
                  }}
                />

                <FileUploaderButton
                  buttonText="Transport vergunning"
                  title="Transport vergunning"
                  initialName="Transport vergunning"
                  file={supplier.documents.find((d) => d.type === SupplierDocumentType.TransportInsurance)?.document}
                  onSubmit={async (document) => {
                    await linkSupplierDocument(supplier.id, document.id, SupplierDocumentType.TransportInsurance);
                    refreshData();
                  }}
                />

                <MultiFileUploaderButton
                  buttonText="Andere"
                  title="Andere"
                  files={supplier.documents.filter((d) => d.type === SupplierDocumentType.Other).map((v) => v.document)}
                  onSubmit={async (documents) => {
                    let count = 0;
                    await Promise.allSettled(
                      documents.map(async (doc) => {
                        try {
                          await linkSupplierDocument(supplier.id, doc.id, SupplierDocumentType.Other);
                          count += 1;
                          toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                        } catch (err) {
                          captureException(err);
                          toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                        }
                      }),
                    );
                    refreshData();
                  }}
                  unlinkDocument={async (docId: string) => {
                    try {
                      await unlinkSupplierDocument(supplier.id, docId, SupplierDocumentType.Other);
                      refreshData();
                      toast.success('Bestand gewist');
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                    }
                  }}
                />
              </div>
            </div>

            <Tabs items={tabItems} />
          </div>
        </SupplierProvider>
      </>
    );
  }
};

export default SupplierPage;
